import { itemColor, requireItem, TIER_COLORS } from "./items.ts";

/**
 * Modelos dos itens (F47): ferramentas, armas, flechas e materiais viram modelos KayKit (pacote `itens`
 * de `tools/assets`) na mão, no chão e nos ícones. Só visual: ids, protocolo e servidor não mudam. Um
 * modelo por forma; a cor do tier (ou do material) pinta as células de metal do atlas no cliente, sem
 * shader novo. Comida, armaduras, construção, estações, utilitários (salvo a tocha, F48) e os
 * materiais de `PROCEDURAL_ITEMS` continuam com a malha procedural.
 */
export interface ItemShapeInfo {
  /**
   * Maior medida do modelo na mão (m, escala 1): o alvo do ajuste no pipeline. Ferramentas e armas têm
   * o cabo ao longo de +y, com o punho na origem.
   */
  readonly length: number;
  /** Pose do ícone (rad, giro XYZ do three): as compridas na diagonal, a cabeça para cima e à direita. */
  readonly icon: readonly [number, number, number];
}

/** Diagonal das peças compridas no ícone: o cabo de baixo à esquerda para cima à direita. */
const DIAGONAL = [0.2, 0.35, -Math.PI / 4] as const;

/**
 * Formas de item com modelo. Tamanhos na mão do boneco (1,8 m de altura); a primeira pessoa, o chão e o
 * ícone reescalam. A pedra é a `pedrinha` dos enfeites do chão (pacote `detalhes`), lida pelo id.
 */
export const ITEM_SHAPES = {
  machado: { length: 0.7, icon: DIAGONAL },
  picareta: { length: 0.75, icon: DIAGONAL },
  marreta: { length: 0.85, icon: DIAGONAL },
  porrete: { length: 0.7, icon: DIAGONAL },
  lanca: { length: 1.5, icon: DIAGONAL },
  // O cajado da Arcanista (F66): o do pack Adventurers.
  cajado: { length: 1.35, icon: DIAGONAL },
  adaga: { length: 0.45, icon: DIAGONAL },
  espada: { length: 0.95, icon: DIAGONAL },
  arco: { length: 1, icon: DIAGONAL },
  flecha: { length: 0.65, icon: DIAGONAL },
  tora: { length: 0.6, icon: [0.45, 0.75, 0] },
  lingote: { length: 0.36, icon: [0.5, -0.6, 0] },
  minerio: { length: 0.3, icon: [0.35, -0.6, 0.2] },
  corda: { length: 0.42, icon: [0.75, 0.3, 0] },
  pedra: { length: 0.4, icon: [0.35, -0.6, 0.2] },
  // A tocha (F48): modelo na mão, no chão e no ícone; não é estrutura.
  tocha: { length: 0.65, icon: DIAGONAL },
} as const satisfies Record<string, ItemShapeInfo>;

export type ItemShape = keyof typeof ITEM_SHAPES;

/** Formas na ordem estável (manifesto do pipeline e testes). */
export const ITEM_SHAPE_IDS = Object.keys(ITEM_SHAPES) as ItemShape[];

/**
 * Cores das tintas (sRGB): os materiais dos tiers 1 a 5 de `TIER_COLORS` e a pederneira à parte (a cor
 * do item). O raio é só azul claro, sem brilho (o brilho fica para a F52).
 */
export const ITEM_TINTS = {
  madeira: TIER_COLORS[1],
  pedra: TIER_COLORS[2],
  ferro: TIER_COLORS[3],
  prata: TIER_COLORS[4],
  raio: TIER_COLORS[5],
  pederneira: requireItem("pederneira").color!,
} as const;

export type ItemTint = keyof typeof ITEM_TINTS;

export interface ItemLook {
  readonly shape: ItemShape;
  /**
   * Cor (sRGB) das partes tingíveis do modelo (cabeça, lâmina, ponta, a peça inteira dos materiais),
   * mantendo o claro e o escuro do atlas; sem ela, as cores do pack.
   */
  readonly tint?: string;
}

/** Ferramenta, arma ou flecha: a cabeça na cor do material do item (não de `tool.tier`). */
const forged = (shape: ItemShape, tint: ItemTint): ItemLook => ({ shape, tint: ITEM_TINTS[tint] });
/** Material: a peça inteira na cor do próprio item. */
const raw = (id: string, shape: ItemShape): ItemLook => ({ shape, tint: itemColor(requireItem(id)) });

/**
 * Forma e tinta por id de item. A tinta vem do item: o Machado e a Picareta de Pedra-de-raio têm
 * `tool.tier` 4, mas são do tier 5 (raio). Nos arcos, a tinta pinta os braços: o de Cerejeira (tier 2,
 * feito de Madeira Nobre) fica com a cor dela, não com o cinza da pedra. O porrete (todo de madeira) e a
 * corda ficam com as cores do modelo.
 */
export const ITEM_LOOKS: Readonly<Record<string, ItemLook>> = {
  // ---- ferramentas ----
  "machado-madeira": forged("machado", "madeira"),
  "machado-pedra": forged("machado", "pedra"),
  "machado-ferro": forged("machado", "ferro"),
  "machado-prata": forged("machado", "prata"),
  "machado-raio": forged("machado", "raio"),
  "picareta-madeira": forged("picareta", "madeira"),
  "picareta-pedra": forged("picareta", "pedra"),
  "picareta-ferro": forged("picareta", "ferro"),
  "picareta-prata": forged("picareta", "prata"),
  "picareta-raio": forged("picareta", "raio"),

  // ---- armas ----
  porrete: { shape: "porrete" },
  "lanca-pau": forged("lanca", "madeira"),
  "lanca-ferro": forged("lanca", "ferro"),
  "lanca-prata": forged("lanca", "prata"),
  // O cajado do pack não tem parte de metal tingível: os três com as cores dele (o tier vai nos pips, F60).
  "cajado-galho": { shape: "cajado" },
  "cajado-ferro": { shape: "cajado" },
  "cajado-prata": { shape: "cajado" },
  "arco-galho": forged("arco", "madeira"),
  "arco-ipe": { shape: "arco", tint: itemColor(requireItem("madeira-de-lei")) },
  "arco-prata": forged("arco", "prata"),
  "arco-trovao": forged("arco", "raio"),
  "facao-pederneira": forged("adaga", "pederneira"),
  "facao-ferro": forged("adaga", "ferro"),
  "marreta-ferro": forged("marreta", "ferro"),
  "marreta-trovejante": forged("marreta", "raio"),
  "espada-prata": forged("espada", "prata"),
  "espada-raio": forged("espada", "raio"),

  // ---- munição ----
  "flecha-pau": forged("flecha", "madeira"),
  "flecha-pederneira": forged("flecha", "pederneira"),
  "flecha-ferro": forged("flecha", "ferro"),
  "flecha-prata": forged("flecha", "prata"),

  // ---- materiais ----
  madeira: raw("madeira", "tora"),
  "madeira-de-lei": raw("madeira-de-lei", "tora"),
  "tora-angelim": raw("tora-angelim", "tora"),
  corda: { shape: "corda" },
  pedra: raw("pedra", "pedra"),
  pederneira: raw("pederneira", "pedra"),
  carvao: raw("carvao", "minerio"),
  "minerio-ferro": raw("minerio-ferro", "minerio"),
  "minerio-prata": raw("minerio-prata", "minerio"),
  "fragmento-raio": raw("fragmento-raio", "minerio"),
  "lingote-ferro": raw("lingote-ferro", "lingote"),
  "lingote-prata": raw("lingote-prata", "lingote"),
  "lingote-raio": raw("lingote-raio", "lingote"),

  // ---- utilitários (F48) ----
  tocha: { shape: "tocha" },
};

/**
 * Materiais que continuam procedurais: não há resina, palha, barro nem couro nos packs. Os demais
 * itens fora de `ITEM_LOOKS` (comida, armaduras, construção, estações, utilitários menos a tocha) também.
 */
export const PROCEDURAL_ITEMS: readonly string[] = ["resina-jatoba", "palha", "barro", "couro"];
